import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Default user model if none exists in localStorage.
const defaultUserModel = {
  preferredTypes: ["Logical Reasoning", "Pattern Recognition"],
  eloRating: 100, // Starting Elo rating
  skillLevel: "Intermediate",
  speedAccuracyBalance: "Neutral",
  userName: "" // We'll store the user's name here.
};

// Helper function to determine the current Elo bracket based on an integer Elo value.
const getEloBracket = (eloRating) => {
  if (eloRating < 101) return { lower: 0, upper: 100 };
  else if (eloRating < 201) return { lower: 101, upper: 200 };
  else if (eloRating < 301) return { lower: 201, upper: 300 };
  else if (eloRating < 401) return { lower: 301, upper: 400 };
  else if (eloRating < 501) return { lower: 401, upper: 500 };
  else if (eloRating < 601) return { lower: 501, upper: 600 };
  else if (eloRating < 701) return { lower: 601, upper: 700 };
  else return { lower: 701, upper: 800 };
};

const HomeScreen = () => {
  const navigate = useNavigate();

  // Retrieve or create user model from localStorage.
  const storedUserModel = localStorage.getItem('userModel');
  let parsedModel = storedUserModel ? JSON.parse(storedUserModel) : { ...defaultUserModel };

  // Round Elo for bracket calculation.
  const currentElo = Math.round(parsedModel.eloRating || 0);

  // Derive bracket & progress for the Elo progress bar.
  const bracket = getEloBracket(currentElo);
  const range = bracket.upper - bracket.lower;
  const progressRaw = ((currentElo - bracket.lower) / range) * 100;
  const progress = Math.max(0, Math.min(100, progressRaw));

  // The user's name from the userModel, defaulting to empty if none found.
  const [username, setUsername] = useState(parsedModel.userName ?? "");

  const handleNavigation = (quizMode) => {
    if (!username.trim()) {
      alert('Please enter your name to personalize your experience.');
      return;
    }
    // Update the userModel with the new or existing name, then store in localStorage.
    parsedModel.userName = username;
    localStorage.setItem('userModel', JSON.stringify(parsedModel));

    // Navigate to the variant selection screen.
    navigate('/quiz-variant', { state: { username, quizMode } });
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Mind Odyssey</h1>
      <p style={styles.subtitle}>Enhance Your Critical Thinking</p>

      {/* Username Input */}
      <input
        type="text"
        placeholder="Enter your name"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        style={styles.input}
      />

      {/* Quiz Buttons */}
      <button style={styles.button} onClick={() => handleNavigation('timed')}>
        Timed Quiz
      </button>
      <button style={styles.button} onClick={() => handleNavigation('normal')}>
        Normal Quiz
      </button>

      {/* Elo Progress Display at the bottom */}
      <div style={styles.eloContainer}>
        <p style={styles.eloText}>Current Elo: {currentElo}</p>
        <div style={styles.rangeContainer}>
          <span style={styles.rangeValue}>{bracket.lower}</span>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${progress}%` }} />
          </div>
          <span style={styles.rangeValue}>{bracket.upper}</span>
        </div>
        <p style={styles.eloText}>
          {progress === 100 && bracket.upper === 800
            ? 'Max Level'
            : `${Math.round(progress)}% to next level`}
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: '#e6f0fa',
    padding: '20px',
  },
  title: {
    fontSize: '3rem',
    color: '#4682b4',
    marginBottom: '0.5rem',
  },
  subtitle: {
    fontSize: '1.5rem',
    color: '#4682b4',
    marginBottom: '1.5rem',
  },
  input: {
    padding: '0.75rem',
    fontSize: '1rem',
    marginBottom: '1.5rem',
    borderRadius: '8px',
    border: '1px solid #4682b4',
    width: '80%',
    maxWidth: '300px',
  },
  button: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    margin: '0.5rem',
    borderRadius: '8px',
    backgroundColor: '#4682b4',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    width: '80%',
    maxWidth: '300px',
  },
  eloContainer: {
    width: '80%',
    maxWidth: '350px',
    marginTop: '1.5rem',
    textAlign: 'center',
  },
  eloText: {
    fontSize: '1rem',
    color: '#4682b4',
    margin: '0.5rem 0',
  },
  rangeContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '0.5rem 0',
  },
  rangeValue: {
    fontSize: '0.9rem',
    color: '#4682b4',
    width: '40px',
    textAlign: 'center',
  },
  progressBar: {
    flex: 1,
    margin: '0 0.5rem',
    height: '20px',
    backgroundColor: '#ccc',
    borderRadius: '10px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#4682b4',
    transition: 'width 0.3s ease-in-out',
  },
};

export default HomeScreen;
